import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Clock, Send, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookTraining() {
  const { user } = useAuth();
  const [interventions, setInterventions] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    intervention_id: '',
    intervention_name: '',
    preferred_date: '',
    notes: ''
  });

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [intRes, sessRes] = await Promise.all([
        axios.get(`${API}/interventions`),
        axios.get(`${API}/my-training-sessions`)
      ]);
      setInterventions(intRes.data);
      setMySessions(sessRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.intervention_id || !form.preferred_date) {
      toast.error('Please select an intervention and preferred date');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/training-sessions`, form);
      toast.success('Training session booked successfully!');
      setForm({ intervention_id: '', intervention_name: '', preferred_date: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book session');
    } finally {
      setSubmitting(false);
    }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-24 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="book-training-page">
      <Navbar />
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-2">Book a Training Session</h1>
          <p className="text-slate-600 mb-10">Schedule a training session with our expert facilitators. Choose an intervention type and your preferred date.</p>

          <div className="bg-white rounded-xl border border-slate-200 p-8 mb-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Intervention Type *</label>
                <div className="relative">
                  <select
                    value={form.intervention_id}
                    onChange={(e) => {
                      const selected = interventions.find(i => i.id === e.target.value);
                      setForm({ ...form, intervention_id: e.target.value, intervention_name: selected?.name || '' });
                    }}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    data-testid="intervention-select"
                  >
                    <option value="">Select an intervention...</option>
                    {interventions.map(i => (
                      <option key={i.id} value={i.id}>{i.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
                {form.intervention_id && interventions.find(i => i.id === form.intervention_id)?.description && (
                  <p className="mt-2 text-sm text-slate-500">
                    {interventions.find(i => i.id === form.intervention_id).description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Date *</label>
                <div className="relative">
                  <input
                    type="date"
                    value={form.preferred_date}
                    min={today}
                    onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    data-testid="preferred-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Additional Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={4}
                  placeholder="Any specific requirements or topics you'd like covered..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                  data-testid="training-notes-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
                data-testid="submit-training-btn"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Booking...' : 'Book Session'}
              </button>
            </form>
          </div>

          {mySessions.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">My Bookings</h2>
              <div className="space-y-4">
                {mySessions.map(session => (
                  <div key={session.id} className="bg-white rounded-xl border border-slate-200 p-6 flex items-center justify-between" data-testid={`session-${session.id}`}>
                    <div>
                      <h3 className="font-medium text-slate-900">{session.intervention_name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-4 h-4" />
                          {session.preferred_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(session.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {session.notes && <p className="text-sm text-slate-500 mt-1">{session.notes}</p>}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${statusColors[session.status] || 'bg-slate-100 text-slate-700'}`}>
                      {session.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
